import React, { useState } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, LabelList } from 'recharts';

// Cores Power BI
const COLORS = {
  primary: "#0078d4", // Azul
  text: "#605e5c",
  grid: "#e1dfdd",
  tooltipBg: "#ffffff"
};

// Formatação Numérica
const parseNumber = (value) => parseFloat(value.replace('.', '').replace(',', '.')) || 0;
const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
const formatNumber = (value) => new Intl.NumberFormat('pt-BR').format(value);

const ScatterChartComponent = ({ data, dataKeys, chartLabel, title }) => {
  // Estado dos vendedores ativos
  const [activeVendors, setActiveVendors] = useState(
    data.reduce((acc, item) => ({ ...acc, [item.VENDEDOR]: true }), {})
  );

  const toggleVendor = (vendorId) => {
    setActiveVendors(prev => ({ ...prev, [vendorId]: !prev[vendorId] }));
  };

  const toggleAll = () => {
      const allActive = Object.values(activeVendors).every(v => v);
      const newState = {};
      data.forEach(item => newState[item.VENDEDOR] = !allActive);
      setActiveVendors(newState);
  };

  // Preparar dados
  const formattedData = data
    .map(item => ({
      ...item,
      x: parseNumber(item[dataKeys[0]]), // Valor Monetário
      y: parseNumber(item[dataKeys[1]]), // Quantidade Itens
      z: 1 
    }))
    .filter(item => activeVendors[item.VENDEDOR]);

  // Tooltip Estilo Power BI
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div className="shadow-lg rounded" style={{ backgroundColor: COLORS.tooltipBg, padding: '12px', border: 'none', minWidth: '200px' }}>
          <p className="mb-2 pb-1 border-bottom fw-bold text-primary">
            {dataPoint.VENDEDOR} - {dataPoint.VENDEDORNOME}
          </p>
          <div className="d-flex justify-content-between mb-1">
             <span className="text-muted small me-3">{chartLabel[0]}:</span>
             <span className="fw-bold">{formatCurrency(dataPoint.x)}</span>
          </div>
          <div className="d-flex justify-content-between">
             <span className="text-muted small me-3">{chartLabel[1]}:</span>
             <span className="fw-bold">{formatNumber(dataPoint.y)}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-100 d-flex flex-column bg-white rounded">
      
      {/* Barra de Filtros (Topo) */}
      <div className="mb-3 pb-3 border-bottom">
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-center">
            <button className="btn btn-sm btn-outline-secondary py-0 px-2" style={{fontSize: '0.8rem'}} onClick={toggleAll}>
                Alternar Todos
            </button>
            {data.map(item => (
                <div key={item.VENDEDOR} 
                     onClick={() => toggleVendor(item.VENDEDOR)}
                     className={`badge border cursor-pointer user-select-none ${activeVendors[item.VENDEDOR] ? 'bg-primary text-white border-primary' : 'bg-light text-muted border-light'}`}
                     style={{ cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {/* Filtro mostra Código E Nome */}
                    <strong>{item.VENDEDOR}</strong>
                    <span style={{ fontWeight: 'normal', opacity: 0.9 }}>- {item.VENDEDORNOME}</span>
                </div>
            ))}
          </div>
      </div>

      {/* Gráfico */}
      <ResponsiveContainer width="100%" height={500}>
        <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />

          {/* Eixo X (Valor) */}
          <XAxis type="number" dataKey="x" name={chartLabel[0]} tickFormatter={(val) => `R$${val}`} 
                 tick={{ fontSize: 12, fill: COLORS.text }} axisLine={{ stroke: COLORS.grid }} tickLine={false}>
            <Label value={chartLabel[0]} offset={-10} position="insideBottom" style={{ fill: COLORS.text, fontSize: '0.8rem' }} />
          </XAxis>

          {/* Eixo Y (Itens) */}
          <YAxis type="number" dataKey="y" name={chartLabel[1]} 
                 tick={{ fontSize: 12, fill: COLORS.text }} axisLine={false} tickLine={false}>
            <Label value={chartLabel[1]} angle={-90} position="insideLeft" style={{ fill: COLORS.text, fontSize: '0.8rem' }} />
          </YAxis>

          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />

          {/* Pontos */}
          <Scatter name={title} data={formattedData} fill={COLORS.primary} shape="circle">
             {/* Alterado para VENDEDOR (apenas código) para não poluir */}
             <LabelList dataKey="VENDEDOR" position="top" style={{ fontSize: '10px', fill: COLORS.text, fontWeight: 'bold' }} />
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScatterChartComponent;